import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Form, Input, DatePicker, Button, Select, InputNumber, Card, Space, Typography, message, Tag, Row, Col, Grid } from 'antd';
import { MinusCircleOutlined, PlusOutlined, SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import client from '../api/client';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title } = Typography;

const JournalEntryForm = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { id } = useParams();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);

    // State for totals
    const [totalDebit, setTotalDebit] = useState(0);
    const [totalCredit, setTotalCredit] = useState(0);
    const [isBalanced, setIsBalanced] = useState(true);
    const autoPairRef = useRef(false);
    const skipAutoPairRef = useRef(true);

    useEffect(() => {
        fetchAccounts();
        if (id) {
            fetchEntry(id);
        } else {
            form.setFieldsValue({
                items: [{}, {}],
                entry_date: dayjs()
            });
            setTimeout(() => {
                skipAutoPairRef.current = false;
            }, 0);
        }
    }, [id]);

    const fetchAccounts = async () => {
        try {
            const { data } = await client.get('/master/coa');
            setAccounts(data.data || data);
        } catch (error) {
            message.error('Failed to load accounts');
        }
    };

    const fetchEntry = async (entryId) => {
        setLoading(true);
        try {
            const { data } = await client.get(`/accounting/journal-entries/${entryId}`);
            form.setFieldsValue({
                ...data,
                entry_date: dayjs(data.entry_date),
                items: data.items.map(item => ({
                    ...item,
                    debit: parseFloat(item.debit),
                    credit: parseFloat(item.credit)
                }))
            });
            calculateTotals(data.items);
            setTimeout(() => {
                skipAutoPairRef.current = false;
            }, 0);
        } catch (error) {
            message.error('Failed to load entry');
        } finally {
            setLoading(false);
        }
    };
    const accountById = useMemo(() => {
        return (accounts || []).reduce((acc, account) => {
            acc[account.id] = account;
            return acc;
        }, {});
    }, [accounts]);

    const normalizeAccountName = (name) => {
        return name.replace(/^A\/D\s*-?\s*/i, '').trim().toLowerCase();
    };

    const isAdAccountName = (name) => {
        return /^A\/D\b/i.test(name);
    };

    const findPairAccount = (account) => {
        if (!account?.name) return null;
        const baseName = normalizeAccountName(account.name);
        if (!baseName) return null;
        if (isAdAccountName(account.name)) {
            return (accounts || []).find((acc) => !isAdAccountName(acc.name || '') && normalizeAccountName(acc.name || '') === baseName) || null;
        }
        return (accounts || []).find((acc) => isAdAccountName(acc.name || '') && normalizeAccountName(acc.name || '') === baseName) || null;
    };

    const ensureAdPairs = (items) => {
        if (autoPairRef.current || skipAutoPairRef.current) return;
        if (!items || !items.length) return;

        const nextItems = [...items];
        let updated = false;

        items.forEach((item) => {
            if (!item?.chart_of_account_id) return;
            const account = accountById[item.chart_of_account_id];
            const debit = Number(item.debit || 0);
            const credit = Number(item.credit || 0);
            if (debit <= 0 && credit <= 0) return;

            const pair = findPairAccount(account);
            if (!pair) return;

            const pairExists = nextItems.some((line) => line?.chart_of_account_id === pair.id);
            if (pairExists) return;

            const isAd = isAdAccountName(account.name || '');
            const pairLine = {
                chart_of_account_id: pair.id,
                debit: 0,
                credit: 0,
                memo: `Auto-paired with ${account.code} - ${account.name}`,
            };

            if (debit > 0) {
                pairLine.credit = debit;
            } else if (credit > 0) {
                pairLine.debit = credit;
            }

            if (isAd) {
                const temp = pairLine.debit;
                pairLine.debit = pairLine.credit;
                pairLine.credit = temp;
            }

            nextItems.push(pairLine);
            updated = true;
        });

        if (updated) {
            autoPairRef.current = true;
            form.setFieldsValue({ items: nextItems });
            setTimeout(() => {
                autoPairRef.current = false;
            }, 0);
        }
    };

    const validateAdPairs = (items) => {
        if (!items || !items.length) return null;
        for (const item of items) {
            if (!item?.chart_of_account_id) continue;
            const account = accountById[item.chart_of_account_id];
            if (!account?.name) continue;

            const debit = Number(item.debit || 0);
            const credit = Number(item.credit || 0);
            if (debit <= 0 && credit <= 0) continue;

            const pair = findPairAccount(account);
            if (!pair) continue;

            const isAd = isAdAccountName(account.name);
            const expectedSide = debit > 0 ? (isAd ? 'debit' : 'credit') : (isAd ? 'credit' : 'debit');

            const pairLine = items.find((line) => line?.chart_of_account_id === pair.id);
            if (!pairLine) {
                return `Missing A/D pair for ${account.code} - ${account.name}.`;
            }

            const pairDebit = Number(pairLine.debit || 0);
            const pairCredit = Number(pairLine.credit || 0);
            if (expectedSide === 'credit' && pairCredit <= 0) {
                return `A/D pair for ${account.code} - ${account.name} must be credited.`;
            }
            if (expectedSide === 'debit' && pairDebit <= 0) {
                return `A/D pair for ${account.code} - ${account.name} must be debited.`;
            }
        }
        return null;
    };

    const onValuesChange = (changedValues, allValues) => {
        if (changedValues.items) {
            calculateTotals(allValues.items);
            ensureAdPairs(allValues.items);
        }
    };

    const calculateTotals = (items) => {
        if (!items) return;
        let d = 0;
        let c = 0;
        items.forEach(item => {
            if (item) {
                d += Number(item.debit || 0);
                c += Number(item.credit || 0);
            }
        });
        setTotalDebit(d);
        setTotalCredit(c);
        setIsBalanced(Math.abs(d - c) < 0.01);
    };

    const onFinish = async (values) => {
        const adError = validateAdPairs(values.items);
        if (adError) {
            message.error(adError);
            return;
        }
        if (!isBalanced) {
            message.error('Entry is not balanced. Debit must equal Credit.');
            return;
        }

        const payload = {
            ...values,
            entry_date: values.entry_date.format('YYYY-MM-DD'),
            items: values.items.map(item => ({
                ...item,
                debit: item.debit || 0,
                credit: item.credit || 0
            }))
        };

        setLoading(true);
        try {
            if (id) {
                await client.put(`/accounting/journal-entries/${id}`, payload);
                message.success('Entry updated successfully');
            } else {
                await client.post('/accounting/journal-entries', payload);
                message.success('Entry created successfully');
            }
            navigate('/accounting/journal-entries');
        } catch (error) {
            console.error(error);
            message.error('Failed to save entry');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/accounting/journal-entries')}
                        style={{ marginBottom: 12, borderRadius: 8 }}
                    >
                        Back to Ledger
                    </Button>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>
                        {id ? "Edit Journal Entry" : "New Journal Entry"}
                    </Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>
                        Record manual adjustments and internal financial transfers.
                    </Typography.Text>
                </div>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                onValuesChange={onValuesChange}
            >
                <Card className="premium-card">
                    <Row gutter={24}>
                        <Col xs={24} md={6}>
                            <Form.Item name="entry_date" label={<Typography.Text strong>Entry Date</Typography.Text>} rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%', height: 40, borderRadius: 8 }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={18}>
                            <Form.Item name="description" label={<Typography.Text strong>General Description / Narrative</Typography.Text>} rules={[{ required: true }]}>
                                <Input style={{ height: 40, borderRadius: 8 }} placeholder="e.g. Monthly depreciation adjustment" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Card className="premium-card" style={{ marginTop: 24 }} title={<Typography.Text strong>Journal Lines</Typography.Text>} bodyStyle={{ padding: 0 }}>
                    <Form.List name="items">
                        {(fields, { add, remove }) => (
                            <div style={{ padding: isMobile ? '0 12px 16px 12px' : '0 24px 24px 24px', overflowX: isMobile ? 'auto' : 'visible' }}>
                                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 12px' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: 'left', padding: '0 8px', color: 'var(--text-secondary)', fontSize: 12, textTransform: 'uppercase' }}>Account</th>
                                            <th style={{ textAlign: 'right', padding: '0 8px', color: 'var(--text-secondary)', fontSize: 12, textTransform: 'uppercase', width: 150 }}>Debit</th>
                                            <th style={{ textAlign: 'right', padding: '0 8px', color: 'var(--text-secondary)', fontSize: 12, textTransform: 'uppercase', width: 150 }}>Credit</th>
                                            <th style={{ textAlign: 'left', padding: '0 8px', color: 'var(--text-secondary)', fontSize: 12, textTransform: 'uppercase' }}>Memo</th>
                                            <th style={{ width: 40 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <tr key={key}>
                                                <td style={{ padding: '0 8px' }}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'chart_of_account_id']}
                                                        rules={[{ required: true, message: 'Required' }]}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <Select
                                                            showSearch
                                                            optionFilterProp="children"
                                                            style={{ height: 40 }}
                                                            placeholder="Select Account"
                                                        >
                                                            {accounts.map(acc => (
                                                                <Option key={acc.id} value={acc.id}>
                                                                    {acc.code} - {acc.name}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                </td>
                                                <td style={{ padding: '0 8px' }}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'debit']}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{ width: '100%', height: 40, borderRadius: 8, textAlign: 'right' }}
                                                            placeholder="0.00"
                                                        />
                                                    </Form.Item>
                                                </td>
                                                <td style={{ padding: '0 8px' }}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'credit']}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{ width: '100%', height: 40, borderRadius: 8, textAlign: 'right' }}
                                                            placeholder="0.00"
                                                        />
                                                    </Form.Item>
                                                </td>
                                                <td style={{ padding: '0 8px' }}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'memo']}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <Input style={{ height: 40, borderRadius: 8 }} placeholder="Reference note" />
                                                    </Form.Item>
                                                </td>
                                                <td style={{ padding: '0 8px', textAlign: 'center' }}>
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<MinusCircleOutlined />}
                                                        onClick={() => remove(name)}
                                                        style={{ borderRadius: 8 }}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <Button
                                    type="dashed"
                                    onClick={() => add()}
                                    block
                                    icon={<PlusOutlined />}
                                    style={{ height: 45, borderRadius: 10, marginTop: 12 }}
                                >
                                    Add New Line
                                </Button>
                            </div>
                        )}
                    </Form.List>
                </Card>

                <div style={{
                    marginTop: 24,
                    padding: '24px 32px',
                    background: '#fff',
                    borderRadius: 16,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Space size="large">
                        <div>
                            <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Debits</Typography.Text>
                            <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-primary)' }}>
                                ₱{totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </div>
                        </div>
                        <div style={{ borderLeft: '1px solid #e2e8f0', height: 40 }}></div>
                        <div>
                            <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Credits</Typography.Text>
                            <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-primary)' }}>
                                ₱{totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </div>
                        </div>
                    </Space>

                    <Space size="middle">
                        {!isBalanced && (
                            <Tag color="error" style={{ borderRadius: 8, padding: '4px 12px' }}>
                                <i className="fa-solid fa-triangle-exclamation"></i> Out of Balance: ₱{Math.abs(totalDebit - totalCredit).toFixed(2)}
                            </Tag>
                        )}
                        {isBalanced && totalDebit > 0 && (
                            <Tag color="success" style={{ borderRadius: 8, padding: '4px 12px' }}>
                                <i className="fa-solid fa-circle-check"></i> Balanced
                            </Tag>
                        )}
                        <Button
                            onClick={() => navigate('/accounting/journal-entries')}
                            style={{ height: 45, borderRadius: 10, padding: '0 24px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            disabled={!isBalanced || totalDebit === 0}
                            icon={<SaveOutlined />}
                            style={{ height: 45, borderRadius: 10, padding: '0 32px', fontWeight: 600 }}
                        >
                            Save & Post Entry
                        </Button>
                    </Space>
                </div>
            </Form>
        </div>
    );
};

export default JournalEntryForm;

